import { modalController, useKeyboard } from "@ionic/vue";

import OrderModal from "@/views/user/order/OrderModal.vue";
import AddProductModal from "@/components/modal/modalControllerModals/AddProductModal.vue";
import DashboardCatalogProduct from "@/components/modal/modalControllerModals/DashboardCatalogProduct.vue";
import NewsModal from "@/components/modal/NewsModal.vue";
import ReadOnlyProduct from "@/components/modal/ReadOnlyProduct.vue";
import FeedbackModal from "@/components/modal/FeedbackModal.vue";
import AiCallModal from "@/components/modal/AiCallModal.vue";

import { OpenProductParams, RegisterData } from "@/types/functions";
import { News, ProductPresentation } from "@/types/notification";
import { CatalogItem } from "@/types/supplier";
import { CustomEventTarget, CustomTouchEvent } from "@/types/ionicEvents";
import { useListStore } from "@/stores/lists";
import { TRACKING_EVENT } from "@/types/enum/notification";
import { useNotificationStore } from "@/stores/notification";
import { useRouteParams } from "@/composables/useRouteParams";
import RegisterEmailForce from "@/components/modal/RegisterEmailForce.vue";

const { isOpen } = useKeyboard()

const openCart = async (locationId?: string): Promise<void> => {
    const listStore = useListStore()
    const modal = await modalController.create({
        component: OrderModal,
        cssClass: 'order-modal'
    })

    if (locationId) modal.onDidDismiss().then(() => {
        listStore.getAllLocationLists(locationId)
    })

    modal.present()
}

const openProduct = async (params: OpenProductParams, showTags = false, okButton = false): Promise<void> => {
    if (isOpen.value) return
    const { product, listItemId } = params

    await (await modalController.create({
        component: DashboardCatalogProduct,
        componentProps: {
            ...params,
            listItemId: listItemId ?? product.id,
            showTags,
            okButton,
        },
        cssClass: 'product-information-modal'
    })).present()
}

const openAddProduct = async (params: OpenProductParams): Promise<void> => {
    if (isOpen.value) return
    const { product, supplierId, listId, offset } = params

    await (await modalController.create({
        component: AddProductModal,
        componentProps: {
            listId,
            supplierId,
            product,
            offset
        },
        breakpoints: [0, 0.3, 0.5, 0.7],
        initialBreakpoint: 0.5
    })).present()
}

const openReadOnly = async (product: CatalogItem): Promise<void> => {
    const modal = await modalController.create({
        component: ReadOnlyProduct,
        componentProps: {
            product
        },
        cssClass: 'read-only-product'
    })

    await modal.present()
}

const openNews = async (information: News | ProductPresentation, productSuggestion?: boolean, noAutoSelect?: boolean): Promise<void> => {
    const notificationStore = useNotificationStore()
    if (productSuggestion) {
        const promotionIndex = notificationStore.productSuggestion.data.findIndex((presentation) => presentation.id === information.id)

        if (promotionIndex === -1) return

        notificationStore.currentInformationIndex = promotionIndex
    }

    await (await modalController.create({
        component: NewsModal,
        componentProps: {
            information,
            productSuggestion,
            noAutoSelect
        },
        cssClass: 'product-information-modal'
    })).present()

    const { locationId } = useRouteParams()

    notificationStore.sendTrackingData({
        locationId: locationId.value,
        informationId: information.id,
        kind: information.kind,
        action: TRACKING_EVENT.CLICK
    })
}

const openFeedback = async (): Promise<void> => {
    const modal = await modalController.create({
        component: FeedbackModal
    })

    await modal.present()
}

const openAiCall = async (): Promise<void> => {
    const modal = await modalController.create({
        component: AiCallModal
    })

    await modal.present()
}

export const openRegisterEmailForce = async (registerData: RegisterData, formInterface: any): Promise<void> => {
    const modal = await modalController.create({
        component: RegisterEmailForce,
        componentProps: {
            registerData,
            formInterface
        }
    })

    await modal.present()
}

const getParentModal = (event: CustomTouchEvent): CustomEventTarget | undefined => {
    if (!event.target) return

    let currentElement: CustomEventTarget = event.target

    while (currentElement.parentElement) {
        if (currentElement.tagName === 'ION-MODAL') return currentElement
        currentElement = currentElement.parentElement
    }
}

const stopEventAtTop = (event: CustomTouchEvent): void => {
    const modal = getParentModal(event)

    if (!modal) return;
    if (modal?.sortedBreakpoints?.length && modal.currentBreakpoint === modal.sortedBreakpoints[modal.sortedBreakpoints.length - 1]) event.stopPropagation()
}

export {
    openCart,
    openAddProduct,
    openProduct,
    openNews,
    stopEventAtTop,
    openReadOnly,
    openFeedback,
    openAiCall
}
